import { gql, useMutation } from '@apollo/client';
import Checkbox from '@orn/shared-web/components/Checkbox/Checkbox';
import * as React from 'react';

import AdminSelectUser from '../../components/admin/AdminSelectUser';
import Button from '../../components/Button';
import Header from '../../components/Header';
import Input from '../../components/Input';
import { useToast } from '../../components/Toast';

import type {
  AdminImpersonateUserSceneMutation,
  AdminImpersonateUserSceneMutationVariables,
} from './__generated__/ImpersonateUserScene.gql';

const ADMIN_IMPERSONATE_USER = gql`
  mutation AdminImpersonateUserScene($input: AdminImpersonateUserInput!) {
    adminImpersonateUser(input: $input) {
      redirectUrl
      token
    }
  }
`;

/**
 * Impersonate-user admin tool. Mints a single-use, 30s-TTL handoff token via
 * the mops-api mutation, then auto-submits a hidden form POST (same-tab) to
 * the consumer app's redemption endpoint (`<WEBAPP_URL>/auth/impersonate` —
 * origin-proxied to the consumer Express server by CloudFront in prod, by
 * Vite's dev-server proxy locally). The redemption endpoint atomically claims
 * the token, sets the consumer-app session cookie, and 302s the operator's
 * tab to the user-product home as the impersonated user.
 *
 * Hidden form POST (rather than GET-with-token-in-URL) keeps the token out of
 * browser history, out of `Referer` headers, and away from corporate
 * URL-scanners that speculatively GET-prefetch links and would race the
 * admin's browser to the redemption endpoint.
 *
 * Same-tab — not `target="_blank"` — because Chrome (and other browsers)
 * send `Origin: null` on cross-site top-level navigations to a new tab as
 * an anti-tracking measure, which fails the redemption endpoint's `Origin`
 * allowlist (the canonical CSRF defense for the form-POST flow). Same-tab
 * cross-site form POSTs preserve the originating Origin header. The
 * operator's MOPS admin session cookie (`_sm`) survives the navigation, so
 * they can hit Back or revisit the mops origin to return.
 */
const ImpersonateUserScene = () => {
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);
  const [reason, setReason] = React.useState('');
  const [hasCertified, setHasCertified] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { showToast } = useToast();

  const [adminImpersonateUser] = useMutation<
    AdminImpersonateUserSceneMutation,
    AdminImpersonateUserSceneMutationVariables
  >(ADMIN_IMPERSONATE_USER);

  const trimmedReason = reason.trim();
  const canSubmit = Boolean(selectedUserId) && hasCertified && trimmedReason.length > 0 && !isSubmitting;

  const handleImpersonate = async () => {
    if (!selectedUserId || !hasCertified || !trimmedReason) {
      showToast({ type: 'error', message: 'Pick a user, write a reason, and certify business need.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await adminImpersonateUser({
        variables: { input: { userId: selectedUserId, reason: trimmedReason } },
      });
      const data = result.data?.adminImpersonateUser;
      if (!data?.redirectUrl || !data.token) {
        showToast({ type: 'error', message: 'Mint succeeded but response was missing the handoff token.' });
        setIsSubmitting(false);
        return;
      }

      // Auto-submit a hidden form same-tab so the token rides in the POST
      // body and the originating Origin header is preserved. The browser
      // navigates this tab to the consumer app as the impersonated user;
      // the MOPS admin session cookie (`_sm`) survives, so the operator
      // can return to mops via the back button or by re-navigating.
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.redirectUrl;
      const tokenInput = document.createElement('input');
      tokenInput.type = 'hidden';
      tokenInput.name = 'token';
      tokenInput.value = data.token;
      form.appendChild(tokenInput);
      document.body.appendChild(form);
      form.submit();
    } catch {
      // ApolloProvider's errorLink reports the error to Sentry but does NOT
      // surface a toast — for a security-sensitive flow we want the operator
      // to see an explicit failure message rather than just having the button
      // silently re-enable. Show a toast and reset submit state so the
      // operator can retry without reloading.
      showToast({ type: 'error', message: 'Impersonation failed — try again.' });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-6 px-6 py-5">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h1 className="text-sm font-semibold uppercase tracking-widest">Impersonate user</h1>
            <p className="text-fg-secondary text-xs">
              Pick a user — this tab will navigate to the consumer app as them. Your MOPS admin session stays alive in
              the background, so you can come back here at any time. The impersonated session lasts 30 minutes.
            </p>
          </div>
          <div className="border-fg-warning-primary flex flex-col gap-1 border-l-2 py-1 pl-3">
            <p className="text-fg-warning-primary text-[10px] font-semibold uppercase tracking-widest">
              Logged and audited
            </p>
            <p className="text-fg-secondary text-xs">
              Every impersonation session is logged; misuse is grounds for disciplinary action.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <AdminSelectUser
            selectedUserId={selectedUserId}
            onSelectionChange={(id) => setSelectedUserId(id)}
            className="w-full max-w-md"
          />
          <Input
            label="Reason"
            placeholder="e.g. investigating support ticket #1234"
            value={reason}
            onChange={setReason}
            className="w-full max-w-md"
          />
          <Checkbox
            isSelected={hasCertified}
            onChange={setHasCertified}
            label="I have a legitimate business need to impersonate this user."
          />
          <div>
            <Button
              onClick={() => void handleImpersonate()}
              isDisabled={!canSubmit}
              isLoading={isSubmitting}
              color="primary"
              size="sm"
            >
              {isSubmitting ? 'Handing off…' : 'Impersonate user'}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ImpersonateUserScene;
